import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux"; // Import useDispatch for dispatching actions

import DynamicForm from "../../components/form/DynamicForm";
import { showToast } from "../../store/slices/toastSlice"; // Import the showToast action

import { getRentalPropertyInputs } from "../../utils/inputs/rental-property.inputs";
import {
  createNewRentalProperty,
  getRentalPropertyDetailsById,
  updateRentalPropertyDetails,
} from "../../services/api/rental-property";
import { fetchBuildingList } from "../../services/api/building";
import { validateRentalPropertyFields } from "../../utils/validation/validation";
import {
  CREATE_UPDATE_BUTTON,
  RESET_CANCEL_BUTTON,
} from "../../styles/ButtonStyles";
import { ROUTE_PATHS } from "../../routes/routePaths";

export default function CreateUpdateRentalProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const dispatch = useDispatch();

  const initialFormState = {
    property_name: "",
    property_address: "",
    building_id: "",
    building_name: "",
  };

  const [propertyDetails, setPropertyDetails] = useState(initialFormState);

  const [buildingOptions, setBuildingOptions] = useState([]);

  // Fetch dropdown options (buildings)
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await fetchBuildingList();
        if (response.responseCode === 200) {
          const dropdownOptions = response.data.map((building) => ({
            label: building.building_name,
            value: building.id,
          }));
          setBuildingOptions(dropdownOptions);
        }
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchBuildings();
  }, [dispatch]);

  // Fetch property details if editing
  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (isEditMode) {
        try {
          const response = await getRentalPropertyDetailsById(id);
          if (response.responseCode === 200) {
            setPropertyDetails(response.data);
          }
        } catch (error) {
          dispatch(showToast({ message: error.message, severity: "error" }));
        }
      }
    };

    fetchPropertyDetails();
  }, [id, isEditMode, dispatch]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "building_id") {
      const selectedBuilding = buildingOptions.find((b) => b.value === value);

      setPropertyDetails((prev) => ({
        ...prev,
        building_id: value,
        building_name: selectedBuilding ? selectedBuilding.label : "",
      }));
    } else {
      setPropertyDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationResult = validateRentalPropertyFields(propertyDetails);
    if (!validationResult.valid) {
      dispatch(
        showToast({ message: validationResult.message, severity: "error" })
      );
      return;
    }

    try {
      let response;
      if (isEditMode) {
        response = await updateRentalPropertyDetails(id, propertyDetails);
      } else {
        response = await createNewRentalProperty(propertyDetails);
      }

      if (response.responseCode === 200) {
        dispatch(showToast({ message: response.message, severity: "success" }));
        localStorage.setItem(
          isEditMode
            ? "editRentalPropertySuccess"
            : "addNewRentalPropertySuccess",
          response.message
        );
        navigate(ROUTE_PATHS.RENTAL_PROPERTY.LIST);
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const formInputs = getRentalPropertyInputs(buildingOptions);

  const handleCancelOrReset = () => {
    if (isEditMode) {
      // Navigate back if editing
      navigate(ROUTE_PATHS.RENTAL_PROPERTY.LIST);
    } else {
      // Reset form if adding
      setPropertyDetails(initialFormState);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          backgroundColor: "#fff",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: 2, color: "#333" }}
        >
          {isEditMode ? "Update Rental Property" : "Add New Rental Property"}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 1, mb: 1 }}
        >
          {isEditMode
            ? "Modify the rental property information below and click update when done."
            : "Fill out the rental property details to add a new record to the system."}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          <DynamicForm
            inputs={formInputs}
            values={propertyDetails}
            onChange={handleChange}
          />

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
            <Button type="submit" variant="contained" sx={CREATE_UPDATE_BUTTON}>
              {isEditMode ? "Update Rental Property" : "Create Rental Property"}
            </Button>

            <Button
              type="button"
              variant="text"
              onClick={handleCancelOrReset}
              sx={RESET_CANCEL_BUTTON}
            >
              {isEditMode ? "Cancel" : "Reset"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
